<template>
  <div class="flex w-full justify-center gap-2">
    <RtCard
      icon="Technology-Wireframe-Calibrate"
      :title="t('rt.tabs.calibration')"
      class="w-83 shrink-0"
    >
      <div class="flex h-full w-full min-h-0 flex-col">
        <p class="text-s-medium leading-5 text-text-glass-default">
          {{ t('rt.calibration.instructions.hold') }}
          <span class="text-black-white">{{ t('rt.calibration.instructions.success') }}</span>
          {{ t('rt.calibration.instructions.simultaneous') }}
          <span class="text-black-white">{{ t('rt.calibration.instructions.save') }}</span>
        </p>

        <div class="flex-1" />

        <Button
          variant="fill"
          shape="corner"
          size="lg"
          :color="action.color"
          class="w-full"
          :disabled="action.disabled"
          :aria-busy="action.pending"
          @click="emit('action')"
        >
          <Icon :icon="action.icon" class="size-4" :class="action.pending && 'animate-spin'" />
          {{ action.label }}
        </Button>
      </div>
    </RtCard>

    <section
      class="glass-frosted flex h-67.5 w-250 flex-1 items-center rounded-8xl p-4"
      :aria-label="t('rt.tabs.calibration')"
      aria-live="polite"
    >
      <div class="mx-auto grid h-full w-full max-w-108 grid-cols-2">
        <article
          v-for="button in buttonItems"
          :key="button.side"
          class="flex min-w-0 flex-col items-center justify-center gap-3"
          :aria-label="`${button.label}：${button.statusText}`"
        >
          <div
            class="flex size-36 items-center justify-center bg-transparent rounded-8xl transition-colors duration-200"
          >
            <Icon
              :icon="button.icon"
              :class="cn('size-30 text-white', button.statusClass)"
              aria-hidden="true"
            />
          </div>

          <p class="flex items-center justify-center gap-1 text-m-medium text-black-white">
            <span>{{ button.label }}</span>
            <span>{{ button.statusText }}</span>
          </p>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Button, cn, Icon } from '@rapoo/element'
  import type { RtSide } from '@rapoo/device-sdk'
  import type { CalibrationPhase } from '../../../composables/useMouseCalibration'
  import type { CalibrationVisualState } from '../../types'
  import RtCard from '../RtCard.vue'

  interface CalibrationButtonMeta {
    side: RtSide
    labelKey: 'rt.common.leftButton' | 'rt.common.rightButton'
    icon: 'VirtualKey-Wireframe-Left' | 'VirtualKey-Wireframe-Right'
  }

  interface CalibrationStateStyle {
    statusClass: string
  }

  interface CalibrationAction {
    label: string
    icon: 'Function-Wireframe-CW' | 'Technology-Wireframe-Calibrate'
    color: 'blue1' | 'blue2' | 'normal'
    disabled: boolean
    pending: boolean
  }

  const BUTTONS: readonly CalibrationButtonMeta[] = [
    {
      side: 'left',
      labelKey: 'rt.common.leftButton',
      icon: 'VirtualKey-Wireframe-Left',
    },
    {
      side: 'right',
      labelKey: 'rt.common.rightButton',
      icon: 'VirtualKey-Wireframe-Right',
    },
  ]

  const STATE_STYLES: Record<CalibrationVisualState, CalibrationStateStyle> = {
    idle: {
      statusClass: 'text-white',
    },
    triggered: {
      statusClass: 'text-blue-light-default',
    },
    passed: {
      statusClass: 'text-green-hover',
    },
  }

  const props = defineProps<{
    phase: CalibrationPhase
    leftState: CalibrationVisualState
    rightState: CalibrationVisualState
    busy?: boolean
  }>()

  const emit = defineEmits<{
    action: []
  }>()

  const { t } = useI18n()

  const isRunning = computed(() => props.phase === 'running')
  const bothPassed = computed(() => props.leftState === 'passed' && props.rightState === 'passed')
  const isPending = computed(
    () => Boolean(props.busy) || props.phase === 'entering' || props.phase === 'saving',
  )

  const action = computed<CalibrationAction>(() => {
    let label = t('rt.calibration.startCalibration')

    if (props.phase === 'entering') {
      label = t('rt.calibration.entering')
    } else if (props.phase === 'saving') {
      label = t('rt.calibration.saving')
    } else if (props.busy && props.phase === 'cancelled') {
      label = t('rt.calibration.cancelling')
    } else if (isRunning.value) {
      label = bothPassed.value
        ? t('rt.calibration.saveCalibration')
        : t('rt.calibration.cancelCalibration')
    }

    return {
      label,
      icon: isPending.value ? 'Function-Wireframe-CW' : 'Technology-Wireframe-Calibrate',
      color: bothPassed.value ? 'blue2' : isRunning.value ? 'blue1' : 'normal',
      disabled: isPending.value,
      pending: isPending.value,
    }
  })

  const statusLabels = computed<Record<CalibrationVisualState, string>>(() => ({
    idle: isRunning.value ? t('rt.calibration.waiting') : t('rt.calibration.notStarted'),
    triggered: t('rt.calibration.calibrating'),
    passed: t('rt.calibration.passed'),
  }))

  const buttonItems = computed(() =>
    BUTTONS.map((button) => {
      const state = button.side === 'left' ? props.leftState : props.rightState

      return {
        ...button,
        ...STATE_STYLES[state],
        label: t(button.labelKey),
        statusText: statusLabels.value[state],
      }
    }),
  )
</script>
